import { computed, effect, Injectable, Signal, signal } from '@angular/core';
import { BaseEntity } from '@core/models';
import { timer } from 'rxjs';

export interface FilterOptions<T extends BaseEntity> {
  filterFunctionSearch?: (item: T, query: string) => boolean;
  applyAdditionalFilters?: (items: T[]) => T[];
}

@Injectable({
  providedIn: 'root',
})
export class PaginationDataService<T extends BaseEntity> {
  // SIGNALS
  private items = signal<T[]>([]);

  // Opciones de filtrado configurables
  private filterOptions: FilterOptions<T> = {};

  // PROPIEDADES COMPUTADAS
  private totalRecords = computed(() => this.filteredItems().length);
  private paginatedItems = computed(() => this.paginateItems());
  private filteredItems = computed(() => this.filter());

  // Ordenamiento
  private sortField = signal<keyof T | null>(null);
  private sortDirection = signal<'asc' | 'desc'>('asc');

  // Paginación
  private currentPage = signal<number>(1);
  private recordsPerPage = signal<number>(20);

  // Búsqueda con debounce
  private searchQuery = signal<string>('');
  private debouncedSearchQuery = signal<string>('');

  constructor() {
    effect(() => {
      const query = this.searchQuery();
      timer(200).subscribe(() => this.debouncedSearchQuery.set(query));
    });
  }

  // GETTER AND SETTERS

  getItemsWithFilter(): Signal<T[]> {
    return this.paginatedItems;
  }

  getItems(): Signal<T[]> {
    return this.items;
  }

  getTotalRecords(): Signal<number> {
    return this.totalRecords;
  }

  getRecordsPerPage(): Signal<number> {
    return this.recordsPerPage;
  }

  getSortField(): Signal<keyof T | null> {
    return this.sortField;
  }

  getSortDirection(): Signal<'asc' | 'desc'> {
    return this.sortDirection;
  }

  setItems(items: T[]): void {
    this.items.set(items);
  }

  setFilterOptions(options: FilterOptions<T>): void {
    this.filterOptions = { ...this.filterOptions, ...options };
  }

  /** Cambiar la página actual */
  setCurrentPage(page: number): void {
    this.currentPage.set(page);
  }

  /** Cambiar la cantidad de registros por página */
  setRecordsPerPage(records: number): void {
    this.recordsPerPage.set(records);
    this.setCurrentPage(1);
  }

  // /** Define el criterio de búsqueda con debounce */
  // setSearchQuery(query: string): void {
  //   this.searchQuery.set(query);
  // }


  toggleSort(field: keyof T) {
    let newDirection: 'asc' | 'desc' | null;

    if (this.sortField() === field) {
      newDirection = this.sortDirection() === 'asc' ? 'desc' : this.sortDirection() === 'desc' ? null : 'asc';
    } else {
      newDirection = 'asc';
    }

    this.setSort(field, newDirection ?? 'asc'); // Si es null, lo tratamos como 'asc'
  }

  private paginateItems(): T[] {
    const filtered = this.filteredItems();
    const startIndex = (this.currentPage() - 1) * this.recordsPerPage();
    return filtered.slice(startIndex, startIndex + this.recordsPerPage());
  }

  private filter(): T[] {
    let filtered = [...this.items()];

    // Filtro por búsqueda
    const query = this.debouncedSearchQuery().toLowerCase();
    if (query && this.filterOptions.filterFunctionSearch) {
      filtered = filtered.filter((item) => this.filterOptions.filterFunctionSearch!(item, query));
    }

    // Aplicar filtros adicionales (si la subclase los define)
    if (this.filterOptions.applyAdditionalFilters) {
      filtered = this.filterOptions.applyAdditionalFilters(filtered);
    }

    // Ordenamiento
    if (this.sortField()) {
      filtered.sort((a, b) => {
        const field = this.sortField()!;
        const valueA = a[field];
        const valueB = b[field];

        return this.sortDirection() === 'asc' ? (valueA < valueB ? -1 : 1) : valueA > valueB ? -1 : 1;
      });
    }

    return filtered;
  }

  /** Define el ordenamiento */
  private setSort(field: keyof T, direction: 'asc' | 'desc'): void {
    this.sortField.set(field);
    this.sortDirection.set(direction);
  }
}
